import { useEffect, useRef, useState } from 'react';

/**
 * Pregunta por el estado cada `intervalMs`. Si una consulta falla, conserva lo último que se vio:
 * una pantalla en el culto nunca debe quedarse en blanco por un corte momentáneo.
 */
export function usePolling<T>(load: (signal: AbortSignal) => Promise<T>, intervalMs: number, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const run = async () => {
      controller = new AbortController();
      try {
        const next = await loadRef.current(controller.signal);
        if (stopped) return;
        setData(next);
        setError(null);
      } catch (e) {
        if (stopped || (e as Error).name === 'AbortError') return;
        setError(e);
      }
      if (!stopped) timer = setTimeout(run, intervalMs);
    };
    void run();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs, tick]);

  return { data, error, setData, refresh: () => setTick((t) => t + 1) };
}
